import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Card, Table } from '@/components_deprecated'
import { format, isSameDay, parseISO } from 'date-fns'
import {
  enforceHourMinuteString,
  stripSecondsFromDuration,
} from '@/modules/activities'
import { Row } from '@/modules/activities/components/overview/ActivitiesWeekTableRow'
import { CheckmarkDiv } from '@/modules/activities/components/overview/CheckmarkDiv'
import styled from 'styled-components'
import { useUser } from '@/utils/hooks'
import { overloadColor } from '@/utils/style'
import { ApproveCommentModal } from '../ApproveCommentModal'
import { AttachToCalendarItemModal } from '../AttachToCalendarItemModal'
import type {
  CalendarItemNode,
  WorkPeriodGroupNode,
  WorkPeriodNode,
} from '../types.graphql'
import { WorkPeriodDetail } from '../WorkPeriodDetail'
import { DietButton } from '../components/DietButton'
import { AddonButton } from '@/modules/activities/components/overview/AddonCell'
import { H3 } from '@/components'

const CalendarItemMarker = styled.div<{ color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 0.5rem 0;
  padding: 1rem;
  border-radius: 0.5rem;
  background: ${props => overloadColor(props.color ?? props.theme.colors.red8)};
  color: ${props => props.theme.colors.white};
  font-size: 1rem;
  line-height: 1rem;
  font-weight: 500;
  text-align: center;
  text-transform: uppercase;
  width: 100px;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
`

const WorkPeriodContainer = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1rem 0;
  width: 100%;

  & > h3 {
    margin: 0;
  }

  & > div > span {
    opacity: 0;
  }
  &:hover {
    & > div > span {
      opacity: 1;
    }
  }
`

const WorkPeriodWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;

  p {
    font-weight: 600;
  }

  ${props => props.theme.media.mobile} {
    width: 100%;
    max-width: 100%;
  }
`

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0;
  gap: 0.5rem;

  ${props => props.theme.media.mobile} {
    width: 100%;
    flex-direction: column;
    width: 100%;
  }
`

const DetachButton = styled.span`
  height: 2rem;
  width: 2rem;
  margin-top: -0.5rem;
  background: ${props => props.theme.colors.gray8};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  i {
    opacity: 0.5;
  }

  &:hover {
    background: ${props => props.theme.colors.gray10};
    i {
      opacity: 1;
    }
  }
  &:active {
    background: ${props => props.theme.colors.gray9};
  }
`

const AttachButton = styled.span`
  height: 2rem;
  width: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    background: ${props => props.theme.colors.red10};
    color: ${props => props.theme.colors.white};
  }
  &:active {
    background: ${props => props.theme.colors.red11};
  }
`

const WorkPeriodGroupInfo = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-left: 0.5rem;

  & > p {
    margin: 5px 0;
  }
`

const StyledTable = styled(Table)`
  margin-left: -1rem;

  tr {
    cursor: default;
  }
`

const StyledRow = styled(Row)`
  height: 14px;
`

const WorkPeriodTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  width: 100%;

  & > h3 {
    margin: 0;
  }

  ${props => props.theme.media.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const ApproveWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  width: 100%;
`

const ApproveColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 50%;
  max-width: 50%;
  background: ${props => props.theme.colors.gray10};
  border-radius: 16px;
`

const ApproveBox = styled.div`
  display: flex;
  gap: 0.25rem;
  padding: 0.5rem;
  height: 4rem;
  align-items: center;
  cursor: pointer;
`

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background: ${props => props.theme.colors.gray8};
`

const ApproveBoxText = styled.p`
  padding: 0.5rem 0.5rem 1rem 0.5rem;
  margin: 0;
`

const TableAndApproveWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 1rem;

  ${props => props.theme.media.mobile} {
    flex-direction: column;
    gap: 0.5rem;
  }
`

const TextAndAction = styled.div<{ border?: boolean }>`
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  border-radius: ${props => props.theme.standardBorderRadius};
  border: ${props =>
    props.border ? `1px solid ${props.theme.colors.gray9}` : 'none'};
  background-color: ${props => props.theme.colors.white};

  & > *:first-child {
    border-right: 1px solid ${props => props.theme.colors.gray9};
    margin-right: 1rem;
    padding-right: 1rem;
  }
  & > *:last-child {
    cursor: pointer;
  }
  ${props => props.theme.media.mobile} {
    width: 100%;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`

const ActionWrapper = styled.div`
  color: ${props => props.theme.colors.gray1};
  background-color: ${props => props.theme.colors.gray8};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  font-size: 18px;
  display: flex;
  justify-content: space-between;
  overflow: hidden;

  & > div {
    width: 100%;
    padding: 0.5rem 1rem;
  }
  & > div:hover {
    background-color: ${props => props.theme.colors.gray7};
    cursor: pointer;
  }
`

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
`

export const WorkPeriodGroupDetails: React.FC<{
  driverPlannerActive: boolean
  group: WorkPeriodGroupNode
  userId: string
  patchWorkPeriod: (
    workPeriodId: string,
    values: Partial<
      Pick<
        WorkPeriodNode,
        | 'approvedByUser'
        | 'approvedByManager'
        | 'userComment'
        | 'managerComment'
      >
    > &
      Partial<{ calendarItem: string | null }>
  ) => void
  onAttachModalOpen?: () => void
  onAttachModalClose?: () => void
  onClose?: () => void
}> = ({
  driverPlannerActive,
  group,
  userId,
  patchWorkPeriod,
  onAttachModalOpen,
  onAttachModalClose,
  onClose,
}) => {
  const addPrompt = usePrompt()

  const me = useUser()

  const translations = useTranslate({
    duration: 'common.duration',
    seeWork: 'common.see-work',
    overtime: 'common.overtime',
    totalWork: 'common.total-work',
    field: 'common.field',
    amount: 'common.amount',
    workPeriod: 'common.work-period',
    paidPause: 'activities.paid-pause',
    salaryCode: 'common.salary-code',
    approvedByDriver: 'common.approved-by-driver',
    approvedByManager: 'common.approved-by-manager',

    noCalendarItemConnected: 'salary.no-calendar-item-connected',

    department: 'common.department',
    facility: 'common.facility',
    addons: 'salary.addons',
  })

  const calendarItem = group?.calendarItem

  const GroupPeriodStart = calendarItem
    ? parseISO(calendarItem.start)
    : group?.workPeriods[0]?.datetimeStart
    ? parseISO(group?.workPeriods[0]?.datetimeStart)
    : new Date()
  const GroupPeriodEnd = calendarItem
    ? parseISO(calendarItem.end)
    : group?.workPeriods[0]?.datetimeEnd
    ? parseISO(group?.workPeriods[0]?.datetimeEnd)
    : new Date()
  const groupIsSameDay =
    GroupPeriodStart && GroupPeriodEnd
      ? isSameDay(GroupPeriodStart, GroupPeriodEnd)
      : false
  const groupStartDate = groupIsSameDay
    ? format(GroupPeriodStart, 'd MMM')
    : format(GroupPeriodStart, 'd MMM HH:mm')
  const groupEndDate = groupIsSameDay
    ? format(GroupPeriodEnd, 'd MMM')
    : format(GroupPeriodEnd, 'd MMM HH:mm')

  const WorkPeriodGroupTitle = groupIsSameDay
    ? `${format(GroupPeriodStart, 'd MMM HH:mm')} - ${format(
        GroupPeriodEnd,
        'HH:mm'
      )}`
    : `${groupStartDate} - ${groupEndDate}`

  const firstWorkPeriod = group?.workPeriods?.[0]

  async function handleAttachToCalendarItem(workPeriodId: string) {
    onAttachModalOpen?.()
    const { data } = await addPrompt<{ id: string } | null>(resolve => (
      <AttachToCalendarItemModal
        workPeriodId={workPeriodId}
        onSubmit={resolve}
      />
    ))

    onAttachModalClose?.()
    if (data === null) return
    patchWorkPeriod(workPeriodId, {
      calendarItem: data?.id ?? null,
    })
  }

  async function handleApproveWorkPeriodDriver(
    workPeriodId: string,
    approved: boolean,
    existingComment: string
  ) {
    if (approved) {
      onAttachModalOpen?.()
      const { data } = await addPrompt<{ comment: string } | null>(resolve => (
        <ApproveCommentModal
          existingComment={existingComment}
          existingApproved={approved}
          onSubmit={resolve}
        />
      ))
      onAttachModalClose?.()
      if (!data) return
      patchWorkPeriod(workPeriodId, {
        approvedByUser: approved,
        userComment: data.comment,
      })
    } else {
      patchWorkPeriod(workPeriodId, {
        approvedByUser: approved,
      })
    }
  }

  async function handleApproveWorkPeriodManager(
    workPeriodId: string,
    approved: boolean,
    existingComment: string
  ) {
    if (approved) {
      onAttachModalOpen?.()
      const { data } = await addPrompt<{ comment: string } | null>(resolve => (
        <ApproveCommentModal
          existingComment={existingComment}
          existingApproved={approved}
          onSubmit={resolve}
        />
      ))
      onAttachModalClose?.()
      if (!data) return
      patchWorkPeriod(workPeriodId, {
        approvedByManager: approved,
        managerComment: data.comment,
      })
    } else {
      patchWorkPeriod(workPeriodId, {
        approvedByManager: approved,
      })
    }
  }

  const isManager = me.id !== group?.workPeriods?.[0]?.user.id

  const color = calendarItem
    ? group?.workPeriods?.every(
        workPeriod => workPeriod.approvedByUser && workPeriod.approvedByManager
      )
      ? 'green'
      : 'orange'
    : group?.workPeriods[0]?.approvedByUser &&
      group?.workPeriods[0]?.approvedByManager
    ? 'green'
    : 'red8'

  async function handleShowWorkPeriod(
    workPeriod: WorkPeriodNode,
    calendarItem?: CalendarItemNode | null
  ) {
    onAttachModalOpen?.()
    const registrationNumbers: string[] = []
    if (calendarItem?.vehicle?.registrationNumber) {
      registrationNumbers.push(calendarItem.vehicle.registrationNumber)
    }
    if (calendarItem?.secondaryVehicle?.registrationNumber) {
      registrationNumbers.push(calendarItem.secondaryVehicle.registrationNumber)
    }
    await addPrompt<{ id: string } | null>(resolve => (
      <WorkPeriodDetail workPeriodId={workPeriod.id} onSubmit={resolve} />
    ))
    onAttachModalClose?.()
  }

  return (
    <FlexWrapper>
      {onClose && (
        <TitleWrapper>
          <FlexRow>
            <ActionWrapper>
              <div onClick={() => onClose()}>
                <Icon icon="arrow-left" onClick={() => onClose()} />
              </div>
            </ActionWrapper>
          </FlexRow>
        </TitleWrapper>
      )}
      <WorkPeriodWrapper>
        {driverPlannerActive && (
          <Card>
            <>
              <TitleWrapper>
                <CalendarItemMarker color={color}>
                  {calendarItem
                    ? calendarItem.title
                    : translations.noCalendarItemConnected}
                </CalendarItemMarker>
                <H3>{WorkPeriodGroupTitle}</H3>
              </TitleWrapper>

              <WorkPeriodGroupInfo>
                {driverPlannerActive && (
                  <>
                    <p>
                      <b>{translations.department}</b>:{' '}
                      {calendarItem?.vehicle?.department?.name ?? ''}
                    </p>
                    <p>
                      <b>{translations.facility}</b>:{' '}
                      {calendarItem?.vehicle?.facility?.name ?? ''}
                    </p>
                    <br />
                    <p>
                      <b>{translations.totalWork}</b>:{' '}
                      {stripSecondsFromDuration(group?.totalWorkTime ?? '')}
                    </p>
                    <p>
                      <b>{translations.paidPause}</b>: {group?.paidPause ?? ''}
                    </p>
                    <Table
                      noBorders
                      noShadow
                      headerMargin
                      rowHoverBackground="gray10"
                    >
                      <thead>
                        <tr>
                          <th>{translations.salaryCode}</th>
                          <th>{translations.amount}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.groupedActivitySums.map(activitySum => (
                          <StyledRow
                            key={activitySum.id}
                            isActive={true}
                            isAbsence={false}
                            onClick={() => {}}
                          >
                            <td>{activitySum?.name}</td>
                            <td>
                              {activitySum.negative ? '-' : ' '}
                              {enforceHourMinuteString(activitySum?.sum)}
                            </td>
                          </StyledRow>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}
              </WorkPeriodGroupInfo>
            </>
          </Card>
        )}
        {group?.workPeriods?.map((workPeriod, j) => {
          const workPeriodStart = parseISO(workPeriod.datetimeStart)
          const workPeriodEnd = parseISO(workPeriod.datetimeEnd)

          const displayDateHours = `${format(
            workPeriodStart,
            'HH:mm'
          )} - ${format(workPeriodEnd, 'HH:mm')}`

          const displayDateDays = `${format(
            workPeriodStart,
            'd MMM HH:mm'
          )} - ${format(workPeriodEnd, 'd MMM HH:mm')}`

          const title = calendarItem
            ? `${translations.workPeriod} ${j + 1} (${format(
                workPeriodStart,
                'd MMM'
              )}): ${displayDateHours}`
            : `${translations.workPeriod}: ${displayDateDays}`

          return (
            <WorkPeriodContainer key={workPeriod.id}>
              <Header>
                <WorkPeriodTitle>
                  {!calendarItem && driverPlannerActive && (
                    <CalendarItemMarker color={color}>
                      <AttachButton
                        onClick={() =>
                          handleAttachToCalendarItem(firstWorkPeriod?.id ?? '')
                        }
                      >
                        <Icon icon="plus" size="24" />
                      </AttachButton>
                    </CalendarItemMarker>
                  )}
                  <ActionWrapper>
                    <div onClick={() => handleShowWorkPeriod(workPeriod)}>
                      <Icon
                        icon="clock"
                        onClick={() => handleShowWorkPeriod(workPeriod)}
                      />
                    </div>
                  </ActionWrapper>
                  <H3>{title}</H3>
                  <DietButton
                    workPeriod={workPeriod}
                    userId={userId}
                    canEdit={true}
                    border={true}
                  />
                  <TextAndAction border={true} onClick={() => {}}>
                    <div>{translations.addons}</div>
                    <AddonButton
                      userId={userId}
                      datetimeStart={workPeriodStart}
                      datetimeEnd={workPeriodEnd}
                      addonsAsText={['']}
                    />
                  </TextAndAction>
                  {calendarItem && (
                    <DetachButton
                      onClick={() =>
                        patchWorkPeriod(workPeriod.id, {
                          calendarItem: null,
                        })
                      }
                    >
                      <i className="fa fa-chain-broken" />
                    </DetachButton>
                  )}
                </WorkPeriodTitle>
              </Header>
              <TableAndApproveWrapper>
                <StyledTable
                  noBorders
                  noShadow
                  headerMargin
                  rowHoverBackground="gray10"
                >
                  <thead>
                    <tr>
                      <th>{translations.salaryCode}</th>
                      <th>{translations.amount}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workPeriod.groupedActivitySums.map(activitySum => (
                      <StyledRow
                        key={activitySum.id}
                        isActive={true}
                        isAbsence={false}
                        onClick={() => {}}
                      >
                        <td>{activitySum?.name}</td>
                        <td>{enforceHourMinuteString(activitySum?.sum)}</td>
                      </StyledRow>
                    ))}
                  </tbody>
                </StyledTable>
                <ApproveWrapper>
                  <ApproveColumn>
                    <ApproveBox
                      onClick={() => {
                        void handleApproveWorkPeriodDriver(
                          workPeriod.id,
                          !workPeriod.approvedByUser,
                          workPeriod.userComment
                        )
                      }}
                    >
                      {translations.approvedByDriver}
                      <CheckmarkDiv
                        checked={workPeriod.approvedByUser}
                        onClick={evt => {
                          evt.preventDefault()
                          evt.stopPropagation()
                          void handleApproveWorkPeriodDriver(
                            workPeriod.id,
                            !workPeriod.approvedByUser,
                            workPeriod.userComment
                          )
                        }}
                      />
                    </ApproveBox>
                    <Separator />
                    <ApproveBoxText>{workPeriod.userComment}</ApproveBoxText>
                  </ApproveColumn>
                  <ApproveColumn>
                    <ApproveBox
                      onClick={() =>
                        isManager
                          ? handleApproveWorkPeriodManager(
                              workPeriod.id,
                              !workPeriod.approvedByManager,
                              workPeriod.managerComment
                            )
                          : null
                      }
                    >
                      {translations.approvedByManager}
                      <CheckmarkDiv
                        checked={workPeriod.approvedByManager}
                        onClick={evt => {
                          if (!isManager) return
                          evt.preventDefault()
                          evt.stopPropagation()
                          void handleApproveWorkPeriodManager(
                            workPeriod.id,
                            !workPeriod.approvedByManager,
                            workPeriod.managerComment
                          )
                        }}
                      />
                    </ApproveBox>
                    <Separator />
                    <ApproveBoxText>{workPeriod.managerComment}</ApproveBoxText>
                  </ApproveColumn>
                </ApproveWrapper>
              </TableAndApproveWrapper>
            </WorkPeriodContainer>
          )
        })}
      </WorkPeriodWrapper>
    </FlexWrapper>
  )
}
